/**
 * Pricing Workspace — Sprint 3 (DEC-048).
 *
 * First-class operational area answering "What should we sell it for?"
 * Sibling to Inventory ("what do we have?") and Purchasing ("what should we
 * buy?"). Per DEC-048 the Pricing & Inventory Centre is organised around
 * these three questions plus Q3 (FIFO cost, future sprint).
 *
 * Sprint 3 · Must Ship (per PO ruling):
 *   • Discount Administration — active discounts across all customers.
 *   • Recent Changes          — Q1 "what has changed recently?"
 *   • Outliers                — Q2 "which discounts deserve attention?"
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  CurrencyDollar, Users, ClockCounterClockwise, Warning, ArrowsClockwise,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCustomerDrawer } from "@/lib/useCustomerDrawer";

const paApi = {
  all:      (params) => api.get("/customer-pricing/admin/discounts", { params }),
  recent:   (params) => api.get("/customer-pricing/admin/recent-changes", { params }),
  outliers: ()       => api.get("/customer-pricing/admin/outliers"),
};

function fmtDT(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}
function fmtRel(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff/60)}m ago`;
  if (diff < 86400) return `${Math.round(diff/3600)}h ago`;
  return `${Math.round(diff/86400)}d ago`;
}

/* -------- Discount Administration -------- */
function DiscountAdminTab({ openCustomer }) {
  const [items, setItems] = useState([]);
  const [product, setProduct] = useState("");
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await paApi.all(product ? { product_code: product } : {});
      setItems(data.items || []);
    } catch (e) { toast.error("Failed to load discounts"); }
    finally { setLoading(false); }
  }, [product]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3" data-testid="pa-admin-tab">
      <div className="flex items-center gap-3">
        <label className="text-xs text-slate-600">Product</label>
        <select value={product} onChange={(e) => setProduct(e.target.value)}
                data-testid="pa-admin-product-filter"
                className="text-xs rounded border border-slate-300 px-2 py-1">
          <option value="">All products</option>
          {["regular","premium","clear_diesel","dyed_diesel","def"].map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <span className="ml-auto text-xs text-slate-500" data-testid="pa-admin-count">{items.length} active discount{items.length === 1 ? "" : "s"}</span>
      </div>
      {loading ? <p className="text-sm text-slate-500">Loading…</p> : (
        <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Customer</th>
                <th className="px-3 py-2 text-left font-medium">Product</th>
                <th className="px-3 py-2 text-right font-medium">Discount</th>
                <th className="px-3 py-2 text-left font-medium">Effective</th>
                <th className="px-3 py-2 text-left font-medium">Updated by</th>
                <th className="px-3 py-2 text-left font-medium">Source</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => openCustomer(r.customer_id)}
                    data-testid={`pa-admin-row-${r.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-800">{r.customer_name || r.customer_id}</div>
                    <div className="text-[10px] text-slate-500">{r.account_number}</div>
                  </td>
                  <td className="px-3 py-2 text-slate-700">{r.product_code}</td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums">${r.discount_amount}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">{fmtDT(r.effective_at)}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">{r.updated_by}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">{r.source}</td>
                </tr>
              ))}
              {items.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-sm text-slate-500">No active discounts yet.</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* -------- Recent Changes -------- */
function RecentChangesTab({ openCustomer }) {
  const [items, setItems] = useState([]);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await paApi.recent({ days });
      setItems(data.items || []);
    } catch (e) { toast.error("Failed to load recent changes"); }
    finally { setLoading(false); }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3" data-testid="pa-recent-tab">
      <div className="flex items-center gap-3">
        <label className="text-xs text-slate-600">Look back</label>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))}
                data-testid="pa-recent-days"
                className="text-xs rounded border border-slate-300 px-2 py-1">
          <option value={1}>Last 24 hours</option>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
        <span className="ml-auto text-xs text-slate-500" data-testid="pa-recent-count">{items.length} change{items.length === 1 ? "" : "s"}</span>
      </div>
      {loading ? <p className="text-sm text-slate-500">Loading…</p> : items.length === 0 ? (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500" data-testid="pa-recent-empty">
          No pricing changes in this window.
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((h) => (
            <li key={h.id} className="rounded border border-slate-200 bg-white p-3 flex items-center gap-3 cursor-pointer hover:border-slate-300"
                onClick={() => openCustomer(h.customer_id)}
                data-testid={`pa-recent-row-${h.id}`}>
              <ClockCounterClockwise size={16} className="text-slate-400" />
              <div className="flex-1">
                <div className="text-sm font-medium text-slate-800">{h.customer_name || h.customer_id} · {h.product_code}</div>
                <div className="text-xs text-slate-600">
                  {h.previous ?? <em className="text-slate-500">none</em>} → <b>{h.new}</b>
                  {h.reason ? <span className="ml-2 text-slate-500">— {h.reason}</span> : null}
                </div>
              </div>
              <div className="text-right text-xs text-slate-500">
                <div>{fmtRel(h.changed_at)}</div>
                <div>{h.changed_by}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* -------- Outliers -------- */
function OutliersTab({ openCustomer }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await paApi.outliers();
      setItems(data.items || []);
    } catch (e) { toast.error("Failed to load outliers"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3" data-testid="pa-outliers-tab">
      <p className="text-xs text-slate-600 max-w-2xl">
        Discounts more than one standard deviation from the per-product mean. This is a management heuristic to surface pricing that deserves review — not a rule to enforce.
      </p>
      <div className="flex items-center gap-3">
        <span className="ml-auto text-xs text-slate-500" data-testid="pa-outliers-count">{items.length} outlier{items.length === 1 ? "" : "s"}</span>
      </div>
      {loading ? <p className="text-sm text-slate-500">Loading…</p> : items.length === 0 ? (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-6 text-center text-sm text-emerald-800" data-testid="pa-outliers-empty">
          No discounts currently outside normal ranges. Portfolio looks consistent.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Customer</th>
                <th className="px-3 py-2 text-left font-medium">Product</th>
                <th className="px-3 py-2 text-right font-medium">Discount</th>
                <th className="px-3 py-2 text-right font-medium">Product mean</th>
                <th className="px-3 py-2 text-right font-medium">Δ σ</th>
                <th className="px-3 py-2 text-left font-medium">Direction</th>
                <th className="px-3 py-2 text-left font-medium">Effective</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={`${r.customer_id}-${r.product_code}`} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => openCustomer(r.customer_id)}
                    data-testid={`pa-outlier-row-${r.customer_id}-${r.product_code}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-800">{r.customer_name || r.customer_id}</div>
                    <div className="text-[10px] text-slate-500">{r.account_number}</div>
                  </td>
                  <td className="px-3 py-2 text-slate-700">{r.product_code}</td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums">${r.discount_amount}</td>
                  <td className="px-3 py-2 text-right text-slate-600 tabular-nums">${r.product_mean}</td>
                  <td className="px-3 py-2 text-right font-bold tabular-nums">{r.delta_sigmas}σ</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${r.direction === "above" ? "bg-rose-100 text-rose-800 border-rose-300" : "bg-sky-100 text-sky-800 border-sky-300"}`}>
                      <Warning size={10} weight="fill" className="mr-1" />
                      {r.direction === "above" ? "above mean" : "below mean"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">{fmtDT(r.effective_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* -------- Root Pricing workspace -------- */
const TABS = [
  { id: "admin",   label: "Discount Administration", icon: CurrencyDollar },
  { id: "recent",  label: "Recent Changes",          icon: ClockCounterClockwise },
  { id: "outliers", label: "Outliers",               icon: Warning },
];

export default function Pricing() {
  const [tab, setTab] = useState("admin");
  const [refreshKey, setRefreshKey] = useState(0);
  const { openCustomer } = useCustomerDrawer();

  return (
    <div className="p-6 space-y-6" data-testid="pricing-workspace">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wide">Pricing</div>
          <h1 className="text-2xl font-semibold text-slate-900">Pricing Administration</h1>
          <p className="text-sm text-slate-600 max-w-2xl mt-1">
            <b>What should we sell it for?</b> Review today's delivered fuel costs and publish today's customer selling prices — the governance view of the pricing engine.
          </p>
        </div>
        <button type="button" onClick={() => setRefreshKey((k) => k + 1)}
                data-testid="pricing-refresh"
                className="text-xs text-slate-700 hover:text-slate-900 inline-flex items-center gap-1 font-medium">
          <ArrowsClockwise size={12} /> Refresh
        </button>
      </header>

      <nav className="flex flex-wrap gap-1 border-b border-slate-200" data-testid="pricing-tabs">
        {TABS.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} data-testid={`pa-tab-${t.id}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-t-md border-b-2 ${
                    tab === t.id ? "border-slate-900 text-slate-900 font-semibold" : "border-transparent text-slate-600 hover:text-slate-900"
                  }`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </nav>

      <section key={refreshKey}>
        {tab === "admin" ? <DiscountAdminTab openCustomer={openCustomer} /> :
         tab === "recent" ? <RecentChangesTab openCustomer={openCustomer} /> :
         <OutliersTab openCustomer={openCustomer} />}
      </section>
    </div>
  );
}
